'use client'
import { signOut, useSession } from 'next-auth/react'
import { useCallback, useEffect, useState, type PropsWithChildren } from 'react'

export function debounceLeading<T extends (...args: any[]) => ReturnType<T>>(callback: T, timeout = 300) {
    let timer: NodeJS.Timeout | undefined
    return (...args: Parameters<T>) => {
        if (timer) {
            return
        }
        timer = setTimeout(() => {
            callback(...args)
            timer = undefined
        }, timeout)
    }
}

const debounceUpdate = debounceLeading((callback: () => void) => {
    callback()
}, 10000)

const _storageKey = '_lastActivity'
// on mount, we will listen to several possible "interactive" events
const windowEvents: WindowActivityEvent[] = ['focus', 'scroll', 'click', 'mousemove']

function storage() {
    return global.window !== undefined ? window.localStorage : null
}

function activity() {
    return new Date().getTime()
}

const parseLastActivityString = (activityStr?: string | null) => {
    if (!activityStr) return null

    const lastActivity = +activityStr

    const now = activity()

    if (lastActivity == null || lastActivity > now || lastActivity <= 0 || Number.isNaN(lastActivity)) {
        // note: some of these conditions could actually mean
        // someone is trying to tamper with your activity timer
        // use with caution
        return null
    }

    return lastActivity
}

const initLastActivity = () => {
    const now = activity()

    const lastActivityStr = storage()?.getItem(_storageKey)

    const lastActivity = parseLastActivityString(lastActivityStr)

    return lastActivity == null ? now : lastActivity
}

type WindowActivityEvent = keyof WindowEventMap

interface AutoLogoutProviderProps extends PropsWithChildren {
    timeoutMs?: number
    timeoutCheckMs?: number
    debug?: boolean
    requireSession?: boolean
}

export function AutoLogoutProvider({
    timeoutMs = 1800000, // Inactivity time 30 min
    timeoutCheckMs = 10000, // Time out to check
    debug = false,
    requireSession = false,
    children
}: AutoLogoutProviderProps) {
    const [lastActivity, setLastActivity] = useState(() => initLastActivity())
    const { data: session, status } = useSession({ required: requireSession })

    const isUserInactive = useCallback(() => {
        const now = activity()

        const sessionObj = session as { createdAt?: string } | null
        const sessionCreatedAt = new Date(sessionObj?.createdAt ?? 0).getTime()

        if (sessionCreatedAt > lastActivity) {
            console.warn('User has logged in======')
            return false
        }

        // maybe verify that they are authenticated?
        if (status === 'authenticated') {
            const expiry = new Date(session?.expires).getTime()

            if (now > expiry) {
                if (debug) {
                    console.debug('User has expired======')
                    console.debug('Expiry', new Date(expiry))
                    console.debug('Now', new Date(now))
                }

                signOut().then()
                return true
            }
        }

        if (lastActivity + timeoutMs < now) {
            console.warn('User inactive')

            if (debug) {
                console.debug('lastActivity', lastActivity)
                console.debug(new Date(lastActivity))

                console.debug('lastActivity + timeoutMs', lastActivity + timeoutMs)
                console.debug(new Date(lastActivity + timeoutMs))

                console.debug('now', now)
                console.debug(new Date(now))

                console.debug('User inactive======', lastActivity, now)
                console.debug('Last activity', new Date(lastActivity))
                console.debug('Now', new Date(now))
            }

            signOut().then()
            return true
        }

        return false
    }, [debug, lastActivity, session, status, timeoutMs])

    const onUserActivity = useCallback(() => {
        return debounceUpdate(() => {
            const now = activity()

            if (debug) {
                console.debug('activity - resetting last activity to ')
                console.debug('now', new Date(now))
            }
            storage()?.setItem(_storageKey, now.toString())
            setLastActivity(now)
        })
    }, [debug])

    useEffect(() => {
        if (status === 'loading') return
        if (status === 'unauthenticated') return

        // no timer has been initialized
        if (timeoutMs === null) {
            return
        }

        // if user is already inactive, do not init
        if (isUserInactive()) {
            return
        }

        const onStorage = ({ key, storageArea, newValue }: StorageEvent) => {
            if (key === _storageKey && storageArea === storage()) {
                // some debugging lines
                if (debug) console.debug('remote tab activity - resetting last activity to ', newValue)
                const lastActivity = parseLastActivityString(newValue)

                if (lastActivity !== null) {
                    setLastActivity(lastActivity)
                }
            }
        }

        const onTimerElapsed = () => isUserInactive()

        windowEvents.forEach((eventName) => {
            window.addEventListener(eventName, onUserActivity, false)
        })

        // we will use localStorage to determine activity
        window.addEventListener('storage', onStorage, false)

        // initialize an interval to check activity
        const intervalId = window.setInterval(onTimerElapsed, timeoutCheckMs)

        return () => {
            // detach and destroy listeners on deconstructor
            windowEvents.forEach((eventName) => {
                window.removeEventListener(eventName, onUserActivity, false)
            })

            window.removeEventListener('storage', onStorage, false)

            window.clearInterval(intervalId)
        }
    }, [debug, status, timeoutCheckMs, timeoutMs, isUserInactive, onUserActivity])

    return children
}
